import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { authClient } from './auth-client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const updateAuth = useCallback((newSession: Session | null) => {
    setUser(newSession?.user ?? null);
    setSession(newSession);
    setLoading(false);
  }, []);

  useEffect(() => {
    authClient.auth.getSession().then(({ data: { session: initialSession } }) => {
      updateAuth(initialSession);
    });

    const { data: { subscription } } = authClient.auth.onAuthStateChange(
      (event, newSession) => {
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          return;
        }
        updateAuth(newSession);
      }
    );

    return () => subscription.unsubscribe();
  }, [updateAuth]);

  const value = useMemo(
    () => ({ user, session, loading }),
    [user, session, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
